import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  ArrowSquareOut,
  CaretRight,
  CheckCircle,
  Clock,
  WarningCircle,
} from "@/components/ui/icons";
import { openExternalUrl } from "@/lib/open-url";
import type { SyncTest, SyncLink } from "@ano/shared/sync/sync-event";

/**
 * Shared section renderers for the Ano Sync timeline card AND the Control
 * Tower panel. Each section renders nothing when its data is empty (the
 * "only show non-empty sections" rule from the brief). `collapsible` drives
 * the card (compact, expandable); the panel passes `collapsible={false}` to
 * show everything inline.
 */

interface SectionShellProps {
  title: string;
  icon: ReactNode;
  count?: number;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

function SectionShell({
  title,
  icon,
  count,
  collapsible,
  defaultOpen,
  children,
}: SectionShellProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  const header = (
    <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      {icon}
      <span>{title}</span>
      {count !== undefined ? (
        <span className="text-muted-foreground/60">· {count}</span>
      ) : null}
    </span>
  );

  if (!collapsible) {
    return (
      <div className="space-y-1">
        {header}
        <div className="space-y-1">{children}</div>
      </div>
    );
  }

  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        className="h-auto w-full justify-start gap-1.5 px-1 py-0.5 hover:bg-foreground/[0.04]"
      >
        <CaretRight
          size={10}
          className={cn(
            "shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        {header}
      </Button>
      {open ? <div className="space-y-1 pl-4 pt-1">{children}</div> : null}
    </div>
  );
}

export interface StringListSectionProps {
  title: string;
  icon: ReactNode;
  items: string[] | undefined;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Render rows in monospace (changed files / commands). */
  mono?: boolean;
  /** Prefix each row (e.g. "$ " for commands). */
  prefix?: string;
  /** Per-row marker icon (decisions/risks/blockers/next steps). */
  rowIcon?: ReactNode;
}

export function StringListSection({
  title,
  icon,
  items,
  collapsible,
  defaultOpen,
  mono,
  prefix,
  rowIcon,
}: StringListSectionProps) {
  if (!items || items.length === 0) return null;
  return (
    <SectionShell
      title={title}
      icon={icon}
      count={items.length}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
    >
      {items.map((item, i) => (
        <div
          key={`${i}-${item}`}
          className="flex items-start gap-1.5 text-[12px] text-foreground/85"
        >
          {rowIcon ? <span className="mt-0.5 shrink-0">{rowIcon}</span> : null}
          <span
            className={cn(
              "min-w-0 break-words",
              mono &&
                "rounded bg-foreground/[0.04] px-1 py-0.5 font-mono text-[11px]",
            )}
          >
            {prefix}
            {item}
          </span>
        </div>
      ))}
    </SectionShell>
  );
}

function testRowIcon(status: SyncTest["status"]) {
  switch (status) {
    case "passed":
      return <CheckCircle size={12} className="text-success" />;
    case "failed":
      return <WarningCircle size={12} className="text-destructive" />;
    case "skipped":
    case "unknown":
    default:
      return <Clock size={12} className="text-muted-foreground" />;
  }
}

export interface TestsSectionProps {
  tests: SyncTest[] | undefined;
  icon: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export function TestsSection({
  tests,
  icon,
  collapsible,
  defaultOpen,
}: TestsSectionProps) {
  if (!tests || tests.length === 0) return null;
  return (
    <SectionShell
      title="Tests"
      icon={icon}
      count={tests.length}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
    >
      {tests.map((t, i) => (
        <div
          key={`${i}-${t.name}`}
          className="flex items-start gap-1.5 text-[12px] text-foreground/85"
        >
          <span className="mt-0.5 shrink-0">{testRowIcon(t.status)}</span>
          <span className="min-w-0 break-words">
            <span className="font-medium">{t.name}</span>
            {t.details ? (
              <span className="text-muted-foreground"> — {t.details}</span>
            ) : null}
          </span>
        </div>
      ))}
    </SectionShell>
  );
}

export interface LinksSectionProps {
  links: SyncLink[] | undefined;
  icon: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export function LinksSection({
  links,
  icon,
  collapsible,
  defaultOpen,
}: LinksSectionProps) {
  if (!links || links.length === 0) return null;
  return (
    <SectionShell
      title="Links"
      icon={icon}
      count={links.length}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
    >
      <div className="flex flex-wrap gap-1.5">
        {links.map((l, i) => (
          <Button
            key={`${i}-${l.url}`}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => openExternalUrl(l.url)}
            className="h-auto gap-1.5 px-2 py-1 text-[11px]"
          >
            <ArrowSquareOut size={12} />
            {l.label}
          </Button>
        ))}
      </div>
    </SectionShell>
  );
}
